/**
 * Conflict Resolution Modal
 * 
 * Displays conflicts and allows users to resolve them by choosing local, server, or merge.
 */

import React, { useState, useEffect } from 'react';
import { View, ScrollView, Pressable, ActivityIndicator, StyleSheet, Alert } from 'react-native';
import { Modal } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { ThemedText, SubHeading, Body, Caption } from '@/components/ui/Text';
import {
  getUnresolvedConflicts,
  getConflictsForTrip,
  resolveConflict,
  autoResolveConflict,
  type Conflict,
  type ConflictResolution,
} from '@/services/conflictResolution';
import { formatDistanceToNow } from 'date-fns';

type ConflictResolutionModalProps = {
  visible: boolean;
  onClose: () => void;
  tripId?: string; // Optional: filter conflicts by trip
  onResolved?: () => void; // Callback when conflicts are resolved
};

export function ConflictResolutionModal({
  visible,
  onClose,
  tripId,
  onResolved,
}: ConflictResolutionModalProps) {
  const theme = useTheme();
  const [conflicts, setConflicts] = useState<Conflict[]>([]);
  const [loading, setLoading] = useState(true);
  const [resolving, setResolving] = useState<string | null>(null);
  const [selectedConflict, setSelectedConflict] = useState<Conflict | null>(null);
  const [showResolutionOptions, setShowResolutionOptions] = useState(false);

  useEffect(() => {
    if (!visible) return;

    const loadConflicts = async () => {
      setLoading(true);
      try {
        const conflictsList = tripId
          ? await getConflictsForTrip(tripId)
          : await getUnresolvedConflicts();
        setConflicts(conflictsList);
      } catch (error) {
        console.error('[ConflictResolutionModal] Error loading conflicts:', error);
        Alert.alert('Error', 'Failed to load conflicts');
      } finally {
        setLoading(false);
      }
    };

    loadConflicts();
  }, [visible, tripId]);

  const handleResolve = async (
    conflict: Conflict,
    resolution: ConflictResolution,
    mergedData?: any
  ) => {
    setResolving(conflict.id);
    try {
      await resolveConflict(conflict.id, resolution, mergedData);
      
      // Remove resolved conflict from list
      setConflicts((prev) => prev.filter((c) => c.id !== conflict.id));
      
      if (onResolved) {
        onResolved();
      }

      // If no more conflicts, close modal
      if (conflicts.length === 1) {
        onClose();
      }
    } catch (error) {
      console.error('[ConflictResolutionModal] Error resolving conflict:', error);
      Alert.alert('Error', 'Failed to resolve conflict. Please try again.');
    } finally {
      setResolving(null);
      setSelectedConflict(null);
      setShowResolutionOptions(false);
    }
  };

  const handleAutoResolve = async (conflict: Conflict, strategy: 'last_write_wins' | 'local_wins' | 'server_wins') => {
    setResolving(conflict.id);
    try {
      await autoResolveConflict(conflict, strategy);
      
      // Remove resolved conflict from list
      setConflicts((prev) => prev.filter((c) => c.id !== conflict.id));
      
      if (onResolved) {
        onResolved();
      }

      // If no more conflicts, close modal
      if (conflicts.length === 1) {
        onClose();
      }
    } catch (error) {
      console.error('[ConflictResolutionModal] Error auto-resolving conflict:', error);
      Alert.alert('Error', 'Failed to auto-resolve conflict. Please try again.');
    } finally {
      setResolving(null);
      setSelectedConflict(null);
      setShowResolutionOptions(false);
    }
  };

  const formatDate = (date: Date): string => {
    try {
      return formatDistanceToNow(date, { addSuffix: true });
    } catch {
      return 'Unknown';
    }
  };

  const getConflictTypeLabel = (type: Conflict['conflictType']): string => {
    switch (type) {
      case 'edit':
        return 'Edit Conflict';
      case 'delete':
        return 'Delete Conflict';
      case 'concurrent_edit':
        return 'Concurrent Edit';
      default:
        return 'Conflict';
    }
  };

  const getConflictTypeIcon = (type: Conflict['conflictType']): string => {
    switch (type) {
      case 'edit':
        return 'create-outline';
      case 'delete':
        return 'trash-outline';
      case 'concurrent_edit':
        return 'git-merge-outline';
      default:
        return 'warning-outline';
    }
  };

  if (!visible) return null;

  return (
    <Modal
      visible={visible}
      animationType="slide"
      transparent
      onRequestClose={onClose}
      presentationStyle="pageSheet"
    >
      <View style={[styles.overlay, { backgroundColor: theme.colors.modalOverlay }]}>
        <Pressable style={styles.backdrop} onPress={onClose} />
        <View
          style={[
            styles.content,
            {
              backgroundColor: theme.colors.surface,
              borderTopLeftRadius: theme.radii.xl,
              borderTopRightRadius: theme.radii.xl,
            },
          ]}
        >
          {/* Header */}
          <View
            style={[
              styles.header,
              {
                borderBottomColor: theme.colors.borderMuted,
              },
            ]}
          >
            <View style={styles.headerLeft}>
              <View
                style={[
                  styles.iconContainer,
                  { backgroundColor: theme.colors.error + '15' },
                ]}
              >
                <Ionicons name="warning" size={24} color={theme.colors.error} />
              </View>
              <View>
                <SubHeading>Resolve Conflicts</SubHeading>
                <Caption color={theme.colors.textSecondary}>
                  {conflicts.length} unresolved conflict{conflicts.length !== 1 ? 's' : ''}
                </Caption>
              </View>
            </View>
            <Pressable onPress={onClose} style={styles.closeButton}>
              <Ionicons name="close" size={24} color={theme.colors.textSecondary} />
            </Pressable>
          </View>

          {/* Content */}
          <ScrollView
            style={styles.scrollView}
            contentContainerStyle={styles.scrollContent}
            showsVerticalScrollIndicator={false}
          >
            {loading ? (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color={theme.colors.primary} />
                <Body color={theme.colors.textSecondary} style={styles.loadingText}>
                  Loading conflicts...
                </Body>
              </View>
            ) : conflicts.length === 0 ? (
              <View style={styles.emptyContainer}>
                <Ionicons name="checkmark-circle" size={64} color={theme.colors.success} />
                <SubHeading style={styles.emptyTitle}>All Conflicts Resolved</SubHeading>
                <Body color={theme.colors.textSecondary} style={styles.emptyText}>
                  There are no unresolved conflicts.
                </Body>
                <Button
                  label="Close"
                  onPress={onClose}
                  style={styles.emptyButton}
                />
              </View>
            ) : (
              <>
                {conflicts.map((conflict) => (
                  <Card key={conflict.id} style={styles.conflictCard}>
                    {/* Conflict Header */}
                    <View style={styles.conflictHeader}>
                      <View style={styles.conflictHeaderLeft}>
                        <Ionicons
                          name={getConflictTypeIcon(conflict.conflictType) as any}
                          size={20}
                          color={theme.colors.error}
                        />
                        <View style={styles.conflictHeaderText}>
                          <Body variant="bodyStrong">
                            {getConflictTypeLabel(conflict.conflictType)}
                          </Body>
                          <Caption color={theme.colors.textSecondary}>
                            {conflict.collection} • {conflict.docId}
                          </Caption>
                        </View>
                      </View>
                    </View>

                    {/* Conflict Details */}
                    <View style={styles.conflictDetails}>
                      <View style={styles.conflictDetailRow}>
                        <Caption color={theme.colors.textSecondary}>Detected</Caption>
                        <Body>{formatDate(conflict.detectedAt)}</Body>
                      </View>
                      {conflict.tripId && (
                        <View style={styles.conflictDetailRow}>
                          <Caption color={theme.colors.textSecondary}>Trip</Caption>
                          <Body>{conflict.tripId}</Body>
                        </View>
                      )}
                    </View>

                    {/* Resolution Options */}
                    {resolving === conflict.id ? (
                      <View style={styles.resolvingContainer}>
                        <ActivityIndicator size="small" color={theme.colors.primary} />
                        <Body color={theme.colors.textSecondary}>Resolving...</Body>
                      </View>
                    ) : (
                      <View style={styles.resolutionActions}>
                        <Button
                          label="Keep Local"
                          variant="secondary"
                          size="small"
                          onPress={() => handleResolve(conflict, 'local')}
                          icon={<Ionicons name="phone-portrait-outline" size={16} color={theme.colors.primary} />}
                          style={styles.resolutionButton}
                        />
                        <Button
                          label="Use Server"
                          variant="secondary"
                          size="small"
                          onPress={() => handleResolve(conflict, 'server')}
                          icon={<Ionicons name="cloud-outline" size={16} color={theme.colors.primary} />}
                          style={styles.resolutionButton}
                        />
                        <Button
                          label="Auto-Resolve"
                          variant="ghost"
                          size="small"
                          onPress={() => handleAutoResolve(conflict, 'last_write_wins')}
                          icon={<Ionicons name="flash-outline" size={16} color={theme.colors.textSecondary} />}
                          style={styles.resolutionButton}
                        />
                      </View>
                    )}
                  </Card>
                ))}
              </>
            )}
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  backdrop: {
    flex: 1,
  },
  content: {
    maxHeight: '90%',
    paddingBottom: 20,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderBottomWidth: 1,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flex: 1,
  },
  iconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  closeButton: {
    padding: 4,
    borderRadius: 8,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
    gap: 12,
  },
  loadingContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
    gap: 12,
  },
  loadingText: {
    marginTop: 8,
  },
  emptyContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
    gap: 12,
  },
  emptyTitle: {
    marginTop: 16,
    textAlign: 'center',
  },
  emptyText: {
    textAlign: 'center',
    marginBottom: 8,
  },
  emptyButton: {
    marginTop: 8,
    width: '100%',
  },
  conflictCard: {
    padding: 16,
    gap: 12,
    borderLeftWidth: 3,
    borderLeftColor: '#FF6B6B', // Error color
  },
  conflictHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  conflictHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flex: 1,
  },
  conflictHeaderText: {
    flex: 1,
    gap: 4,
  },
  conflictDetails: {
    gap: 8,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: 'rgba(0,0,0,0.1)',
  },
  conflictDetailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  resolvingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: 12,
  },
  resolutionActions: {
    flexDirection: 'row',
    gap: 8,
    flexWrap: 'wrap',
  },
  resolutionButton: {
    flex: 1,
    minWidth: 100,
  },
});

